/**
 * @file ImageOrDefault.tsx
 *
 * Renders a product image with a skeleton shimmer while it loads, falling back
 * to the shared placeholder graphic (`public/placeholder.svg`, same box via the
 * shared `className`) when there is no source or the image fails to load. Keeps
 * the list and card layouts stable regardless of missing or broken imagery.
 */

import { useCallback, useState } from 'react';

const PLACEHOLDER_SRC = '/placeholder.svg';

interface ImageOrDefaultProps {
  src?: string;
  alt: string;
  className?: string;
}

/**
 * Renders an image (skeleton while loading) or a same-size placeholder graphic
 * on missing/broken source.
 *
 * @param props.src - Image URL; omit or invalid to show the placeholder.
 * @param props.alt - Alternative text for the image.
 * @param props.className - Shared class controlling the box dimensions.
 *
 * @returns The image or placeholder element.
 */
export function ImageOrDefault({ src, alt, className }: ImageOrDefaultProps) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  // Cached images can be complete before the load event is observed.
  const captureRef = useCallback((img: HTMLImageElement | null) => {
    if (img?.complete && img.naturalWidth > 0) {
      setLoaded(true);
    }
  }, []);

  if (!src || failed) {
    return <img src={PLACEHOLDER_SRC} alt="" aria-hidden="true" className={className} />;
  }

  return (
    <img
      ref={captureRef}
      src={src}
      alt={alt}
      className={`${className ?? ''}${loaded ? '' : ' gv-skeleton'}`}
      loading="lazy"
      onLoad={() => setLoaded(true)}
      onError={() => setFailed(true)}
    />
  );
}
